import { type ReactNode, useState } from 'react';
import { Banner, Box, Button, Flex, InlineStack, Paragraph, Text, TextField } from '@customerio/pluma-components/react';
import { formatCentsToUsd } from '@/utils/currency';
import { isEmptyString } from '@/utils/string';
import { readInputValue } from '@/utils/dom';
import { BILLING_PATHS } from '../utils/paths';
import { nextBillDateLabel } from '../utils/next-bill-date';
import type { CreditCardFormAddress } from '../utils/credit-card-form-submit';

/**
 * The checkout-only extras layered onto the base card form: an applied-coupon banner, an optional
 * coupon-entry field, the "What you owe today" totals, and a "Back to plans" button. Omitted on the
 * payment-methods "update card" flow, where the form is just card + address. The totals values are
 * computed by the caller (the checkout signup form); this form only displays them.
 */
export type CheckoutFormOptions = {
  /** The applied coupon's display name; shows the success banner when present. */
  appliedCouponName?: string;
  /** Prefilled coupon-input value. */
  couponCode?: string;
  /** Whether to render the coupon-entry field (the Ordway coupon flow). */
  showCouponInput?: boolean;
  isApplyingCoupon?: boolean;
  onApplyCoupon?: (code: string, address: CreditCardFormAddress) => void;
  /** Whether to show the "What you owe today" block (the caller already accounts for future start dates). */
  showTotalDue?: boolean;
  total?: number;
  dueToday?: number | null;
  dueTodayPreSavings?: number | null;
  isPriceLoading?: boolean;
  isMonthlyInterval?: boolean;
  isNextBillOveragesOnly?: boolean;
  /** Whether to show the "Back to plans" button. */
  canCancel?: boolean;
  cancelActionLabel?: string;
  /**
   * Notified with the full address and the field that changed whenever a field changes, so the caller
   * can refresh the tax rate. The field lets the caller skip fields that cannot change the rate.
   */
  onBillingAddressChange?: (address: CreditCardFormAddress, changedField: keyof CreditCardFormAddress) => void;
  /** Whether a tax-rate refresh is in flight; blocks submit against a stale total. */
  isTaxRateLoading?: boolean;
};

/**
 * The coupon "Apply" is enabled once the coupon text and required address fields are present (Ember
 * parity) — and only when the account may make the billing write it triggers (`canApply` folds in the
 * billing-edit permission, masquerade read-only, and verification-rejected guards the submit enforces).
 */
function isApplyCouponDisabled(
  checkout: CheckoutFormOptions,
  couponInput: string,
  address: CreditCardFormAddress,
  canApply: boolean,
): boolean {
  return (
    !canApply ||
    (checkout.isApplyingCoupon ?? false) ||
    couponInput.trim() === '' ||
    isEmptyString(address.addressLine1) ||
    isEmptyString(address.city) ||
    isEmptyString(address.country) ||
    isEmptyString(address.zipCode)
  );
}

type CheckoutCouponBannerProps = {
  checkout?: CheckoutFormOptions;
};

/** The success banner shown once a coupon has been applied. Renders nothing until one is. */
export function CheckoutCouponBanner({ checkout }: CheckoutCouponBannerProps) {
  if (checkout?.appliedCouponName == null) {
    return null;
  }
  return (
    <Banner
      data-test-cc-form-coupon
      variant="success"
      title="Discount successfully applied!"
      description={`You'll receive ${checkout.appliedCouponName}.`}
      mb="150"
    />
  );
}

type CheckoutCouponSectionProps = {
  checkout?: CheckoutFormOptions;
  address: CreditCardFormAddress;
  /** Whether the account may apply a coupon (billing-edit permission, not masquerading, not rejected). */
  canApply: boolean;
};

/** The coupon-code entry row (Ordway coupon flow only). Renders nothing outside that flow. */
export function CheckoutCouponSection({ checkout, address, canApply }: CheckoutCouponSectionProps) {
  const [couponInput, setCouponInput] = useState(() => checkout?.couponCode ?? '');

  if (checkout?.showCouponInput !== true) {
    return null;
  }

  const applyDisabled = isApplyCouponDisabled(checkout, couponInput, address, canApply);
  const applyCoupon = () => {
    if (applyDisabled) {
      return;
    }
    checkout.onApplyCoupon?.(couponInput.trim(), address);
  };

  return (
    <InlineStack data-test-cc-form-coupon alignBlock="flex-end" gap="150" mt="150">
      <TextField
        label="Coupon"
        value={couponInput}
        isDisabled={!canApply}
        onInput={(event) => setCouponInput(readInputValue(event))}
        // The coupon field sits inside the payment <Form>; a bare Enter would submit "Pay now" at the
        // undiscounted price, so intercept it and apply the coupon instead.
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            applyCoupon();
          }
        }}
      />
      <Button
        // Explicit type="button" so clicking Apply never submits the surrounding payment <Form>.
        type="button"
        variant="secondary"
        isLoading={checkout.isApplyingCoupon ?? false}
        isDisabled={applyDisabled}
        onClick={applyCoupon}
      >
        Apply
      </Button>
    </InlineStack>
  );
}

type CheckoutTotalsProps = {
  checkout: CheckoutFormOptions;
};

/** The "What you owe today" summary with the amount due and (for monthly plans) the next billing date. */
function CheckoutTotals({ checkout }: CheckoutTotalsProps) {
  const amountDue = checkout.dueToday ?? checkout.total ?? 0;

  return (
    <Box mr="400">
      <Paragraph size="lg" mb="100" data-test-cc-total-owed>
        What you owe today:{' '}
        {checkout.isPriceLoading === true ? (
          <Text>Loading…</Text>
        ) : (
          <>
            {checkout.dueTodayPreSavings != null ? (
              <Text color="secondary" style={{ textDecoration: 'line-through' }}>
                {formatCentsToUsd(checkout.dueTodayPreSavings)}
              </Text>
            ) : null}{' '}
            <Text weight="bold">{formatCentsToUsd(amountDue)}</Text> including tax.
          </>
        )}
      </Paragraph>
      {checkout.isMonthlyInterval === true ? (
        <Paragraph text="product-pxs" textAlign="right" data-test-cc-next-bill-date>
          Next billing date: {nextBillDateLabel()}
          {checkout.isNextBillOveragesOnly === true ? ' (overages only)' : ''}
        </Paragraph>
      ) : null}
    </Box>
  );
}

type FormFooterProps = {
  checkout?: CheckoutFormOptions;
  canEdit: boolean;
  /** The submit button (owned by the form, which holds the submit state). */
  submitButton: ReactNode;
  /**
   * A lower-emphasis action sitting to the left of the submit, e.g. a Cancel that closes an inline
   * form. Pluma orders actions by importance, most important on the right, so this renders first.
   */
  secondaryAction?: ReactNode;
};

/**
 * The form's footer row. On the payment-methods flow it's just the right-aligned submit button; in
 * checkout it also shows the "Back to plans" button and the "What you owe today" totals.
 */
export function FormFooter({ checkout, canEdit, submitButton, secondaryAction }: FormFooterProps) {
  if (checkout == null) {
    return (
      <Flex justifyContent="flex-end" alignItems="center" gap="150" mt="400">
        {secondaryAction}
        {submitButton}
      </Flex>
    );
  }

  // The left `Flex flex={1}` spacer is always rendered (the "Back to plans" button sits inside it only
  // when cancellable), so it absorbs the free space and the totals + submit button always group on the
  // right — matching Ember. Without the spacer, a footer with no cancel button (e.g. promo checkout)
  // would split the totals to the far left and the button to the far right.
  return (
    <Flex mt="400" alignItems="flex-start">
      <Flex flex={1}>
        {checkout.canCancel === true ? (
          <Button
            data-test-back-to-plans-button
            href={BILLING_PATHS.availablePlans}
            isDisabled={!canEdit}
            icon="chevron-left"
          >
            {checkout.cancelActionLabel ?? 'Back to plans'}
          </Button>
        ) : null}
      </Flex>
      {checkout.showTotalDue === true ? <CheckoutTotals checkout={checkout} /> : null}
      {submitButton}
    </Flex>
  );
}
